import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Breadcrumbs from '~/components/Breadcrumbs'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const BreadcrumbsPage: Component = () => {
  const simpleItems = [{ title: 'Panel' }, { title: 'Configuración' }, { title: 'Privacidad', active: true }]

  const linkItems = [
    { title: 'E-commerce', href: '#' },
    { title: 'Electrónica', href: '#' },
    { title: 'Smartphones', active: true }
  ]

  const multiLevelItems = [
    { title: 'Recursos', href: '#' },
    { title: 'Documentación', href: '#' },
    { title: 'Frontend', href: '#' },
    { title: 'SolidJS', href: '#' },
    { title: 'Guía de Inicio', active: true }
  ]

  return (
    <>
      <Title>Breadcrumbs Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Breadcrumbs</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Las migas de pan (breadcrumbs) ayudan a los usuarios a navegar por la jerarquía del sitio y proporcionan un rastro de dónde se encuentran actualmente.
          </p>
        </div>

        <div class="space-y-12">
          {/* Jerarquía Simple */}
          <Card>
            <Card.Header
              title="Jerarquía de Navegación"
              subtitle="El uso más común del componente para mostrar la ruta de navegación actual."
              icon={<Icon icon="material-symbols-light:alt-route-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-8 p-10 bg-surface-muted rounded-xl border border-border shadow-inner">
                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Solo Texto</h4>
                  <div class="p-6 bg-surface rounded-lg shadow-sm">
                    <Breadcrumbs items={simpleItems} />
                  </div>
                </div>

                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Con Enlaces Interactivos</h4>
                  <div class="p-6 bg-surface rounded-lg shadow-sm">
                    <Breadcrumbs items={linkItems} />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Rutas Profundas */}
          <Card>
            <Card.Header
              title="Estructuras Complejas"
              subtitle="Navegación en aplicaciones de múltiples niveles o sistemas de archivos."
              icon={<Icon icon="material-symbols-light:folder-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 bg-surface-muted rounded-xl border border-border">
                <div class="flex items-center gap-4 mb-6">
                  <div class="p-3 bg-surface rounded-lg shadow-sm">
                    <Icon icon="material-symbols-light:description-outline" class="text-foreground-subtle" size={24} />
                  </div>
                  <div>
                    <h4 class="font-bold text-foreground">Visor de Documentación</h4>
                    <p class="text-xs text-foreground-subtle">Ruta de archivo actual</p>
                  </div>
                </div>
                <div class="p-8 bg-surface rounded-xl shadow-md border border-border">
                  <Breadcrumbs items={multiLevelItems} />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Mejores Prácticas */}
          <Card>
            <Card.Header
              title="Diseño Adaptable"
              subtitle="Consejos para integrar breadcrumbs en layouts modernos."
              icon={<Icon icon="material-symbols-light:maximize" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-6 border border-border rounded-xl bg-surface-muted">
                  <h4 class="font-bold mb-4 flex items-center gap-2">
                    <Icon icon="material-symbols-light:left-panel-open-outline" size={18} class="text-primary-500" /> Cabeceras de Sección
                  </h4>
                  <p class="text-sm text-foreground-subtle mb-6 leading-relaxed">Ubica los breadcrumbs inmediatamente debajo o encima de los títulos de página para contextualizar rápidamente.</p>
                  <div class="bg-surface p-4 rounded-lg border border-border shadow-sm">
                    <Breadcrumbs items={linkItems.slice(0, 2)} />
                    <h2 class="text-xl font-bold mt-2">Iphone 15 Pro</h2>
                  </div>
                </div>
                <div class="p-6 border border-border rounded-xl bg-surface-muted">
                  <h4 class="font-bold mb-4 flex items-center gap-2">
                    <Icon icon="material-symbols-light:web" size={18} class="text-indigo-500" /> Barras de Herramientas
                  </h4>
                  <p class="text-sm text-foreground-subtle mb-6 leading-relaxed">Puedes usarlos en barras laterales o de herramientas superiores para indicar la ubicación en sistemas complejos.</p>
                  <div class="bg-surface p-4 rounded-lg border border-border shadow-sm flex items-center justify-between">
                    <Breadcrumbs items={[{ title: 'Apps' }, { title: 'Admin', active: true }]} />
                    <Button variant="soft" size="sm" color="secondary" startIcon="material-symbols-light:settings-outline">
                      Ajustes
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Especificaciones técnicas de Breadcrumbs." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">items</td>
                      <td class="px-6 py-4 text-foreground-subtle font-bold">BreadcrumbItemType[]</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Array de objetos con la definición de cada paso en la ruta.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">separator</td>
                      <td class="px-6 py-4 text-foreground-subtle">string | JSX.Element</td>
                      <td class="px-6 py-4 font-mono">'/'</td>
                      <td class="px-6 py-4 text-foreground-muted">Caracter o elemento visual que separa los niveles.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">class</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4 font-mono">''</td>
                      <td class="px-6 py-4 text-foreground-muted">Clases personalizadas para el contenedor nav.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-10 space-y-4">
                <h4 class="font-bold text-foreground">BreadcrumbItemType Interface</h4>
                <div class="bg-surface-muted p-6 rounded-xl border border-border font-mono text-xs">
                  <p>
                    <span class="text-blue-500">interface</span> <span class="text-emerald-500">BreadcrumbItemType</span> {'{'}
                  </p>
                  <p class="ml-4">
                    <span class="text-purple-500">title</span>: string;
                  </p>
                  <p class="ml-4">
                    <span class="text-purple-500">href</span>?: string;
                  </p>
                  <p class="ml-4">
                    <span class="text-purple-500">active</span>?: boolean;
                  </p>
                  <p class="ml-4">
                    <span class="text-purple-500">icon</span>?: string;
                  </p>
                  <p>{'}'}</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default BreadcrumbsPage
